import { useParams } from "react-router-dom";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { DetailSummary } from "@/shared/components/ui/DetailSummary";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { PageHeader, PageHeaderAction } from "@/shared/components/ui/PageHeader";
import { RowSkeleton } from "@/shared/components/ui/RowSkeleton";
import { Section } from "@/shared/components/ui/Section";
import { StatusBadge } from "@/shared/components/ui/StatusBadge";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { useService } from "../hooks/serviceQueries";
import { formatServiceMinutes, formatServicePrice } from "../utils/serviceFormatters";
import { ServiceFormsSection } from "../components/ServiceFormsSection";

export function ServiceDetailsPage() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const { goToServiceEdit } = useAppNavigation();
  const { business } = useCurrentBusiness();
  const { data: service, isLoading, error, refetch } = useService(serviceId);

  return (
    <Page>
      <PageHeader
        title={service?.name ?? "Service details"}
        actions={service && !error && <PageHeaderAction aria-label="Edit service" onClick={() => goToServiceEdit(service.id)}>Edit</PageHeaderAction>}
      />
      <PageContent className="space-y-[26px]">
        {isLoading ? <RowSkeleton /> : error ? (
          <ErrorState message={error.message} onRetry={() => void refetch()} />
        ) : !service ? <EmptyState message="Service not found" /> : (
          <>
            <Section title="Service information" card>
              <div className="divide-y divide-border">
                <DetailSummary className="pb-3.5 [&_dd]:text-right" items={[
                  { label: "Availability", value: <StatusBadge tone={service.isActive ? "positive" : "neutral"}>{service.isActive ? "Active" : "Inactive"}</StatusBadge> },
                ]} />
                <dl className="space-y-1.5 py-3.5 text-sm">
                  <dt className="text-muted-foreground">Description</dt>
                  <dd className="whitespace-pre-wrap [overflow-wrap:anywhere]">{service.description || "No description added."}</dd>
                </dl>
                <DetailSummary className="pt-3.5 [&_dd]:text-right" items={[
                  { label: "Price", value: formatServicePrice(service.basePrice, business?.currency) },
                ]} />
              </div>
            </Section>
            <ServiceFormsSection formIds={service.formIds} attachedForms={service.forms} />
            <Section title="Timing" card>
              <div className="divide-y divide-border">
                {[
                  { label: "Duration", value: formatServiceMinutes(service.durationMinutes) },
                  { label: "Buffer before", value: formatServiceMinutes(service.bufferBeforeMinutes) },
                  { label: "Buffer after", value: formatServiceMinutes(service.bufferAfterMinutes) },
                ].map((item) => <DetailSummary key={item.label} className="py-3.5 first:pt-0 last:pb-0 [&_dd]:text-right" items={[item]} />)}
              </div>
              <p className="text-xs leading-relaxed text-muted-foreground">Buffers reserve preparation and wrap-up time, separate from the service duration.</p>
            </Section>
            <Section title="Display" card>
              <DetailSummary className="[&_dd]:text-right" items={[{
                label: "Color",
                value: <span className="inline-flex items-center gap-2">
                  {service.color && <span aria-hidden="true" className="size-[22px] shrink-0 rounded-full border border-border" style={{ backgroundColor: service.color }} />}
                  {service.color || "Not set"}
                </span>,
              }]} />
            </Section>
          </>
        )}
      </PageContent>
    </Page>
  );
}
